import { useMemo } from "react";

import LeaveIconsPicker from "../../../LeaveIconsPicker/LeaveIconsPicker";
import {
  InputRadio,
  InputSelect,
  InputText,
  Label,
} from "components/v2";

import { LEAVE_TYPES_STRINGS } from "../../constants/strings";

import { useIncidenceTypes } from "shared/hooks";
import { useQueryError } from "hooks/useQueryError";

import type { CreateLeaveTypeFormValuesT } from "../../domain/types/leaveType.types";

type FormStepOnePropsT = {
  values: Pick<CreateLeaveTypeFormValuesT, "isJustified" | "code" | "name" | "incidenceTypeId" | "icon">;
  errors: Partial<Record<keyof Pick<CreateLeaveTypeFormValuesT, "isJustified" | "code" | "name" | "incidenceTypeId" | "icon">, string>>;
  handleChange: (e: React.ChangeEvent<unknown>) => void;
  setFieldValue: (field: string, value: unknown) => void;
  isCustom: boolean;
};

const FormStepOne = ({ values, errors, handleChange, setFieldValue, isCustom }: FormStepOnePropsT) => {
  const { data, isLoading, isError, error } = useIncidenceTypes();

  useQueryError({
    isError: isError,
    error: error,
    message: LEAVE_TYPES_STRINGS.INCIDENCE_TYPES_ERROR,
  });

  const memoizedOptions = useMemo(
    () =>
      data?.map((incidenceType) => ({
        id: incidenceType.id,
        label: `${incidenceType.code} - ${incidenceType.name}`,
      })) ?? [],
    [data],
  );

  return (
    <div className="flex flex-col w-full p-2 gap-2">
      <InputRadio
        label={LEAVE_TYPES_STRINGS.IS_JUSTIFIED_LABEL}
        name="isJustified"
        checkedValue={values.isJustified}
        onChange={handleChange}
        error={errors?.isJustified}
        disabled={isCustom === false}
        radios={[
          {
            id: "is-justified-yes",
            label: LEAVE_TYPES_STRINGS.IS_JUSTIFIED_YES,
            value: true,
          },
          {
            id: "is-justified-no",
            label: LEAVE_TYPES_STRINGS.IS_JUSTIFIED_NO,
            value: false,
          },
        ]}
        isRequired
        inline
      />
      <div className="flex gap-2">
        <InputText
          id="code"
          name="code"
          label={LEAVE_TYPES_STRINGS.CODE_LABEL}
          placeholder={LEAVE_TYPES_STRINGS.CODE_PLACEHOLDER}
          value={values.code}
          onChange={handleChange}
          error={errors?.code}
          maxLength={5}
          keyFilter="alphanum"
          disabled={isCustom === false}
          isRequired
        />
        <InputText
          id="name"
          name="name"
          label={LEAVE_TYPES_STRINGS.NAME_LABEL}
          placeholder={LEAVE_TYPES_STRINGS.NAME_PLACEHOLDER}
          value={values.name}
          onChange={handleChange}
          error={errors?.name}
          maxLength={100}
          isRequired
        />
      </div>
      <InputSelect
        id="incidence-type-id"
        name="incidenceTypeId"
        label={LEAVE_TYPES_STRINGS.INCIDENCE_TYPE_LABEL}
        placeholder={LEAVE_TYPES_STRINGS.INCIDENCE_TYPE_PLACEHOLDER}
        value={values.incidenceTypeId}
        onChange={handleChange}
        error={errors?.incidenceTypeId}
        options={memoizedOptions}
        optionLabel="label"
        optionValue="id"
        isLoading={isLoading}
        disabled={isLoading || isError || isCustom === false}
        isRequired
      />
      {!values.isJustified && (
        <div className="flex h-auto flex-col gap-1 xl:flex-1 2xl:flex-1">
          <Label text={LEAVE_TYPES_STRINGS.SELECT_ICON_LABEL} isRequired />
          <LeaveIconsPicker
            selectedIcon={values.icon}
            onIconClick={(iconName: string) => setFieldValue("icon", iconName)}
          />
          {errors?.icon && (
            <span className="text-red-500 text-sm">{errors.icon}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default FormStepOne;
